<template>
    <Teleport to="body">
    <div v-if="isOpen" class="modal sp-modal-card is-active">
        <div class="modal-background" @click="close"></div>
        <div class="modal-card upgrade-modal-card">
            <header class="modal-card-head">
                <p class="modal-card-title">Upgrade to Timist Plus</p>
                <div class="buttons">
                    <button class="button is-small" aria-label="close" @click="close">
                        <span class="icon is-small">
                            <FontAwesomeIcon :icon="faXmark" />
                        </span>
                    </button>
                </div>
            </header>
            <section class="modal-card-body upgrade-modal-body">
                <!-- Compact feature list -->
                <p class="has-text-weight-medium mb-2">Everything in Free, plus:</p>
                <ul class="upgrade-features mb-5">
                    <li v-for="f in PLUS_FEATURES" :key="f.label" class="upgrade-features__item">
                        <span class="icon is-small has-text-success mr-1">
                            <FontAwesomeIcon :icon="faCheck" />
                        </span>
                        <span>
                            <strong>{{ f.label }}</strong>
                            <span> — {{ f.description }}</span>
                        </span>
                    </li>
                </ul>

                <hr class="my-0">

                <!-- Detailed feature sections -->
                <div class="mt-5">
                    <div v-for="feature in PLUS_DETAIL_FEATURES" :key="feature.heading" class="upgrade-detail-feature mb-6">
                        <p class="is-size-7 has-text-weight-semibold has-text-grey is-uppercase mb-2">{{ feature.heading }}</p>
                        <p class="has-text-weight-semibold mb-2">{{ feature.label }}</p>
                        <p class="is-size-6 has-text-grey mb-3">{{ feature.body }}</p>
                        <div class="upgrade-screenshot" :style="`aspect-ratio: ${feature.ratio}`">
                            <picture>
                                <source :srcset="feature.darkImg" media="(prefers-color-scheme: dark)">
                                <img :src="feature.lightImg" :alt="feature.alt" loading="lazy" decoding="async" class="upgrade-screenshot__img">
                            </picture>
                        </div>
                    </div>
                </div>
            </section>
            <footer class="modal-card-foot upgrade-modal-foot">
                <p class="is-size-5 mb-1">
                    <span v-if="trialDays">Try free for {{ trialDays }} days</span>
                    <span v-else>Try free</span>
                    <span v-if="priceDisplay">, then <strong>{{ priceDisplay }}/year</strong></span>
                </p>
                <p class="help mb-3">Local currency options available at checkout. Tax may apply depending on your location.</p>
                <div class="buttons">
                    <button
                        class="button is-primary"
                        @click="upgrade"
                        :class="{ 'is-loading': isUpgrading }"
                        :disabled="isUpgrading"
                    >
                        Start free trial
                    </button>
                    <button class="button" @click="close" :disabled="isUpgrading">
                        Maybe later
                    </button>
                </div>
            </footer>
        </div>
    </div>
    </Teleport>
</template>

<script setup>
import { ref, watch } from 'vue';
import axios from 'axios';
import { useBlocksStore } from '../stores/blocks';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faXmark, faCheck } from '@fortawesome/free-solid-svg-icons';

const PLUS_FEATURES = [
    { label: 'Projects and tags', description: 'Organize blocks with @projects and #tags — dimensions for analytics.' },
    { label: 'Notes', description: "Markdown notes on each block — what got done, what's next." },
    { label: 'Context timer', description: "Runs underneath your blocks. Track what mode you're in, not just what you're doing." },
    { label: 'Analytics', description: 'Planned vs. actual, by project and mode. The picture builds over time.' },
    { label: 'Full history', description: 'Free users see 7 days. Plus gives you everything.' },
];

const PLUS_DETAIL_FEATURES = [
    {
        heading: 'Context timer',
        label: 'The layer underneath',
        body: "Blocks capture what you're doing. The context timer tracks what mode you're in — client work, deep focus, meetings. The gap between context time and block time is the time you didn't track, and that number is worth knowing.",
        lightImg: 'https://static.timist.app/homepage/context-timer-light.png',
        darkImg: 'https://static.timist.app/homepage/context-timer-dark.png',
        alt: 'Context timer running in the bottom bar with an orange indicator, calendar showing context bars alongside time blocks',
        ratio: '24 / 13',
    },
    {
        heading: 'Projects, tags, and notes',
        label: 'Structure without overhead',
        body: "Projects and tags are simple labels — just start typing. Notes live on each block: what you got done, where you're stuck, what's next. Enough structure to be useful, not enough to become a second job.",
        lightImg: 'https://static.timist.app/homepage/projects-tags-notes-light.png',
        darkImg: 'https://static.timist.app/homepage/projects-tags-notes-dark.png',
        alt: 'Timer detail showing project label, tags, and expanded markdown notes with formatting',
        ratio: '24 / 13',
    },
    {
        heading: 'Analytics',
        label: 'Where your time actually went',
        body: "Planned versus actual. Time by project, by mode, the gaps where nothing was tracked. Once you've been blocking for a while, having a record of where it actually went is a nice thing to have.",
        lightImg: 'https://static.timist.app/homepage/analytics-light.png',
        darkImg: 'https://static.timist.app/homepage/analytics-dark.png',
        alt: 'Timist analytics view with KPI cards, time-by-project chart, context overlay, and project distribution donut',
        ratio: '2 / 1',
    },
];

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['close']);

const store = useBlocksStore();

const trialDays = ref(null);
const priceDisplay = ref(null);
const isUpgrading = ref(false);
const pricingLoaded = ref(false);

watch(() => props.isOpen, async (isOpen) => {
    if (!isOpen || pricingLoaded.value) return;
    pricingLoaded.value = true;
    try {
        const { data } = await axios.get('/api/pricing');
        trialDays.value = data.plans?.plus?.trial_days ?? null;
        const plus = data.prices?.find(p => p.lookup_key === 'timist_plus_yearly');
        if (plus?.unit_amount) priceDisplay.value = `$${Math.round(plus.unit_amount / 100)}`;
    } catch {
        pricingLoaded.value = false; // allow retry on next open if the request failed
    }
});

const close = () => emit('close');

const upgrade = async () => {
    isUpgrading.value = true;
    try {
        await store.createCheckoutSession('plus');
    } finally {
        isUpgrading.value = false;
    }
};
</script>

<style scoped>
.upgrade-modal-card {
    max-height: 90vh;
}

.upgrade-modal-body {
    overflow-y: auto;
}

.upgrade-features {
    list-style: none;
    margin: 0;
    padding: 0;
}

.upgrade-features__item {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
    padding: 0.2rem 0;
}

.upgrade-screenshot {
    width: 100%;
    border-radius: 6px;
    overflow: hidden;
    background: var(--bulma-scheme-main-bis, #f5f5f5);
}

.upgrade-screenshot__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.upgrade-modal-foot {
    flex-direction: column;
    align-items: flex-start;
}
</style>
